import styled from "@emotion/styled";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Eye, MagnifyingGlass } from "@phosphor-icons/react";
import { Button, DeleteModal, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import { useCampaignsPaginated, useDeleteCampaign } from "features/finances";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import { useCentralStore } from "stores";
import { EthiopianDateField } from "../../components/EthiopianDateField";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const CampaignTable = ({ onOpenModal, onEdit }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);
  const deleteCampaignMutation = useDeleteCampaign();

  const campaignQuery = useCampaignsPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    queries: {
      search: debouncedSearch,
      startDate,
      endDate
    }
  });

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const navigate = useNavigate();

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(row);
  };

  const handleMenuClose = event => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEdit = event => {
    event.stopPropagation();
    onEdit(selectedCampaign);
    handleMenuClose();
  };

  const handleDelete = event => {
    event.stopPropagation();
    openDeleteModal();
    handleMenuClose();
  };

  const columns = [
    {
      field: "campaign_name",
      headerName: "Name",
      flex: 1
    },
    {
      field: "purpose",
      headerName: "Purpose",
      flex: 1
    },
    {
      field: "chart_of_account",
      headerName: "Account",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{row.chart_of_account?.account_name}</span>;
      }
    },
    {
      field: "goal_amount",
      headerName: "Goal",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{moment(row.start_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{moment(row.end_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={e => {
                e.stopPropagation();
                navigate(`/finances/temp/pledge/${row.id}`);
              }}
            >
              <Eye />
            </IconButton>
            <IconButton
              onClick={event => handleMenuOpen(event, row)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
        );
      }
    }
  ];

  return (
    <Container>
      <DeleteModal
        title="Delete Campaign"
        name={selectedCampaign?.campaign_name || ""}
        onDelete={() => {
          deleteCampaignMutation.mutate({
            churchId: selectedChurch?.id,
            campaignId: selectedCampaign?.id
          });
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Title>Campaign Table</Title>
          <PrimaryActions>
            <Button onClick={onOpenModal}>Create Campaign</Button>
          </PrimaryActions>
        </Header>
        <SecondaryActions>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EthiopianDateField
              size="small"
              label="Start date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <EthiopianDateField
              size="small"
              label="End date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Stack>
          <TextField
            neutral={1}
            placeholder="Search campaign"
            register={() => {}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              )
            }}
            autoComplete="off"
            onChange={e => setSearch(e.target.value)}
            width="auto"
          />
        </SecondaryActions>
      </Stack>
      <DataGrid
        autoHeight
        rows={campaignQuery.data?.result?.data || []}
        rowCount={campaignQuery.data?.result?.total || 0}
        loading={campaignQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
